import TextArea from "formiojs/components/textarea/TextArea";
import React, { Component, createRef, RefObject } from 'react';
import ReactDOM from 'react-dom/client';
import { Editor, EditorTools, EditorUtils, ProseMirror, EditorMountEvent } from '@progress/kendo-react-editor';

// Define interfaces for our component
interface KendoEditorWrapperProps {
    initialValue: string;
    disabled: boolean;
    height: number;
    onChange: (value: string) => void;
    formio: any;
}

interface KendoEditorWrapperState {
    value: string;
    disabled: boolean;
}

// Editor interface for Form.io integration
interface EditorInstance {
    root: any;
    editorRef: RefObject<Editor>;
    setValue: (value: string) => void;
    destroy: () => void;
}

// Import all required tools from EditorTools
const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Subscript,
    Superscript,
    ForeColor,
    BackColor,
    CleanFormatting,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    NumberedList,
    BulletedList,
    Undo,
    Redo,
    FontSize,
    FontName,
    FormatBlock,
    Link,
    Unlink,
    InsertImage,
    ViewHtml,
    InsertTable,
    SelectAll,
    Print,
    Pdf,
    TableProperties,
    TableCellProperties,
    AddRowBefore,
    AddRowAfter,
    AddColumnBefore,
    AddColumnAfter,
    DeleteRow,
    DeleteColumn,
    DeleteTable,
    MergeCells,
    SplitCell
} = EditorTools;

//To apply css to the iframe 
const styles = `
    p {
        font-family:"Open Sans","Roboto",sans-serif;
    }
`;

declare const OSjs: any;

// Create a custom plugin to handle image uploads
const createImageUploadPlugin = (formio) => {
    return new ProseMirror.Plugin({
        props: {
            handleDOMEvents: {
                paste: (view, event) => handleImageEvent(view, event, formio),
                drop: (view, event) => handleImageEvent(view, event, formio)
            }
        },
        key: new ProseMirror.PluginKey('oxzion-image-upload-plugin')
    });
};


const handleImageEvent = (view, event, formio) => {
    const items = (event.clipboardData && event.clipboardData.items) ||
        (event.dataTransfer && event.dataTransfer.files);

    if (!items) return false;

    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
        const item = items[i] as any;
        if (item.type && item.type.indexOf('image') !== -1) {
            const file = 'getAsFile' in item ? item.getAsFile() : item;
            if (file) files.push(file);
        }
    }

    if (!files.length) return false;

    event.preventDefault();

    files.forEach(file => {

        const fileName = `${Date.now()}-${file.name}`;

        let restClient = OSjs.make("oxzion/restClient");
        restClient.request("v1", 'app/file/ckattachment', { file }, "fileupload")
            .then(response => {
                // Insert the image into the editor when upload completes
                if (response.data && response.data.url) {
                    const imageNode = view.state.schema.nodes.image.create({
                        src: response.data.url
                    });
                    const transaction = view.state.tr.replaceSelectionWith(imageNode);
                    view.dispatch(transaction);
                }
            })
            .catch(error => {
                console.error('Error uploading image:', error);
            });
    });

    return true;
};

// Create a React component for the Kendo Editor
class KendoEditorWrapper extends Component<KendoEditorWrapperProps, KendoEditorWrapperState> {
    editorRef: RefObject<Editor> = createRef<Editor>();

    constructor(props: KendoEditorWrapperProps) {
        super(props);
        this.state = {
            value: props.initialValue,
            disabled: props.disabled || false
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleMount = this.handleMount.bind(this);
    }

    handleChange(event: { html: string }): void {
        const newValue = event.html;
        this.setState({ value: newValue });
        this.props?.onChange(newValue);
    }

    // Handle editor mounting to add the custom plugin
    handleMount(event: EditorMountEvent): any {
        const state = event.viewProps.state;
        const {imageResizing} = EditorUtils
        
        const plugins = [
            ...state.plugins,
            createImageUploadPlugin(this.props.formio),
            imageResizing()
        ];

        const iframeDocument = event.dom.ownerDocument;
        const style = iframeDocument.createElement('style');
        style.appendChild(iframeDocument.createTextNode(styles));
        iframeDocument.head.appendChild(style);

        return new ProseMirror.EditorView(
            { mount: event.dom },
            {
                ...event.viewProps,
                state: ProseMirror.EditorState.create({
                    doc: state.doc,
                    plugins
                })
            }
        );
    }

    setHtml(html: string): void {
        if (this.editorRef.current?.view) {
            EditorUtils.setHtml(this.editorRef.current.view, html);
        }
    }

    setDisabled(disabled: boolean): void {
        this.setState({ disabled });
    }

    render(): React.ReactNode {
        return React.createElement(Editor as any, {
            tools: [
                [Bold, Italic, Underline, Strikethrough],
                [Subscript, Superscript],
                ForeColor,
                BackColor,
                [CleanFormatting],
                [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                [Indent, Outdent],
                [OrderedList, UnorderedList],
                [NumberedList, BulletedList],
                FontSize,
                FontName,
                FormatBlock,
                [SelectAll],
                [Undo, Redo],
                [Link, Unlink, InsertImage, ViewHtml],
                [InsertTable],
                [Print],
                [TableProperties, TableCellProperties],
                [AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter],
                [DeleteRow, DeleteColumn, DeleteTable],
                [MergeCells, SplitCell]
            ],
            contentStyle: {
                height: this.props.height || 250,
                width: '100%',
                resize: 'vertical'
            },
            defaultContent: this.state.value,
            onChange: this.handleChange,
            disabled: this.state.disabled,
            ref: this.editorRef,
            onMount: this.handleMount
        });
    }
}

export default class TextAreaComponent extends TextArea {
    editors: EditorInstance[] = [];
    editorsReady: Promise<EditorInstance>[] = [];
    KENDO_EDITORS = ['kendo', 'ckeditor'];

    static editForm(): any {
        // Get the standard TextArea edit form
        const form = super.editForm();

        // Find the 'Display' tab in the edit form
        const tabs = form.components.find((component: any) => component.key === 'tabs');
        if (tabs && tabs.components) {
            const displayTab = tabs.components.find((tab: any) => tab.key === 'display');

            if (displayTab && displayTab.components) {
                // Find the existing editor field to add our option
                const editorField = displayTab.components.find((field: any) => field.key === 'editor');

                if (editorField && editorField.data && editorField.data.values) {
                    // Add Kendo option to the editor dropdown
                    editorField.data.values.push({
                        label: 'Kendo',
                        value: 'kendo'
                    });
                }

            }
        }

        return form;
    }

    attachElement(element: HTMLElement, index: number): HTMLElement {
        // Create a container for the React component
        if (this.KENDO_EDITORS?.includes(this.component.editor)) {
            const container = this.ce('div', { class: 'kendo-editor-container' }) as HTMLElement;
            element.appendChild(container);

            // Create a promise to track when editor is ready
            this.editorsReady[index] = new Promise<EditorInstance>((editorReady) => {

                const initialValue = this.setConvertedValue(this.dataValue, index) || '';

                // Create a ref to the wrapper component
                const wrapperRef = createRef<KendoEditorWrapper>();

                const editorComponent = React.createElement(KendoEditorWrapper, {
                    initialValue: initialValue,
                    disabled: this.options.readOnly || this.disabled,
                    height: this.component.rows * 25,
                    onChange: (newValue: string) => {
                        this.updateEditorValue(index, newValue);
                    },
                    ref: wrapperRef,
                    formio: this
                });

                const root = ReactDOM.createRoot(container);
                root.render(editorComponent);

                // Store a reference to the root for cleanup
                const editorInstance: EditorInstance = {
                    root: root,
                    editorRef: wrapperRef.current?.editorRef || createRef<Editor>(),
                    setValue: (value: string) => {
                        // Use the EditorUtils to directly update HTML content
                        if (wrapperRef.current) {
                            wrapperRef.current.setHtml(this.setConvertedValue(value, index) || '');
                        }
                    },
                    destroy: () => {
                        setTimeout(() => {
                            root.unmount();
                        }, 0);
                    }
                };

                // Store references for the Form.io component
                this.editors[index] = editorInstance;

                // Resolve the promise with the editor reference
                editorReady(editorInstance);
            });

            return element;
        }
        else {
            // Use the default TextArea implementation for other editor types or none
            return super.attachElement(element, index);
        }
    }

    setValueAt(index: number, value: any, flags: any = {}): void {
        if (this.KENDO_EDITORS?.includes(this.component.editor)) {
            this.editors[index] && !flags.skipWysiwyg ? this.editors[index].setValue(value) : null;
        }
        else {
            super.setValueAt(index, value, flags);
        }
    }

    detach(): void {
        if (this.KENDO_EDITORS?.includes(this.component.editor)) {
            let editor = this.editors[0];
            (editor && editor.destroy) ? this.editors[0].destroy() : null;
        }
        else {
            super.detach();
        }
    }
}
